import React from 'react';
import { T } from '../constants';
import { fmtTime, dirStyle, sizeCategory } from '../helpers';
import GradeBadge from './badges/GradeBadge';
import ActionBadge from './badges/ActionBadge';
import SystemBadge from './badges/SystemBadge';
import CategoryBadge from './badges/CategoryBadge';
import MLBar from './badges/MLBar';
import ClaudeVerdictBadge from './badges/ClaudeVerdictBadge';
import SlotLeaders from './SlotLeaders';

export default function SignalCard({ d, selected, onClick, showSystem, leaders }) {
  // TIER strategies expose row kind in `action` ("entry"/"exit") and the verdict
  // in `action_label` ("TRADE"/"BLOCK"/"EXIT"/"OBSERVE"). Other strategies put the
  // verdict in `action` directly. Prefer `action_label` when present.
  const actionEff = d.action_label || d.action;
  const isTrade   = actionEff === 'TRADE';
  const isObserve = actionEff === 'OBSERVE';
  const isStop    = actionEff === 'STOP LOSS';
  const accentColor = isTrade ? T.bull : isObserve ? T.purple : isStop ? T.bear : T.textMuted;
  const sc = sizeCategory(d);   // Concentrate size weight (1.5/1.0/0.5×) for sizing before entry

  return (
    <div
      onClick={onClick}
      className={`signal-card${selected ? ' selected' : ''}`}
      style={{ borderLeftColor: selected ? T.blue : accentColor }}
    >
      {/* Row 1: identifier + action */}
      <div className="signal-card-row">
        {showSystem
          ? <SystemBadge systemId={d.system_id} />
          : <span className="mono" style={{ fontSize: 10, fontWeight: 700, color: T.textDim }}>
              {d.strategies_fired?.[0] || d.src || '—'}
            </span>
        }
        <ActionBadge action={actionEff} />
      </div>

      {/* Row 2: grade + category + time */}
      <div className="signal-card-row">
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <GradeBadge grade={d.grade} decision={d} />
          <ClaudeVerdictBadge verdict={d.claude_verdict} score={d.claude_score} reasoning={d.claude_reasoning} />
          <CategoryBadge category={d.category} />
          {sc && (
            <span className="badge" title={sc.tip} style={{
              background: `${sc.color}18`, border: `1px solid ${sc.color}40`,
              color: sc.color, fontSize: 9, padding: '1px 5px', letterSpacing: '0.04em' }}>
              {sc.catLabel}
            </span>
          )}
          {d.direction && (
            <span className="badge" style={{ ...dirStyle(d.direction), fontSize: 9, padding: '1px 5px' }}>
              {d.direction}
            </span>
          )}
        </div>
        <span className="mono" style={{ fontSize: 10, color: T.textMuted }}>{fmtTime(d.signal_ts)}</span>
      </div>

      {/* Row 3: ML bar */}
      <MLBar score={d.ml_score} />

      {/* Row 4: detail line */}
      {isTrade ? (
        <div style={{ fontSize: 10, color: T.bull, fontFamily: 'Roboto Mono, monospace' }}>
          {d.contracts}c
          {d.entry_price ? ` · entry $${parseFloat(d.entry_price).toLocaleString()}` : ''}
          {(d.stop_price || d.stop_pts || (d.cat_stop_bp != null && d.entry_price != null)) && (
            <span style={{ color: T.bear }}>
              {d.stop_price
                ? ` · SL $${parseFloat(d.stop_price).toLocaleString()}`
                : d.stop_pts
                  ? ` · SL ${d.stop_pts}pt`
                  : (() => {
                      const offset = parseFloat(d.entry_price) * parseFloat(d.cat_stop_bp) / 10000;
                      const side   = (d.side || d.direction || '').toLowerCase();
                      const stopPx = side === 'short'
                        ? parseFloat(d.entry_price) + offset
                        : parseFloat(d.entry_price) - offset;
                      return ` · SL $${stopPx.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    })()
              }
            </span>
          )}
          {d.target_price && (
            <span style={{ color: T.bull }}>
              {` · TP $${parseFloat(d.target_price).toLocaleString()}`}
            </span>
          )}
        </div>
      ) : isObserve ? (
        <div style={{ fontSize: 10, color: T.purple, fontFamily: 'Roboto Mono, monospace' }}>
          {d.trigger ? `trigger: ${d.trigger}` : 'observing'}
        </div>
      ) : d.gate_blocked ? (
        <div style={{ fontSize: 10, color: T.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {d.gate_blocked}
        </div>
      ) : null}

      {/* Row 5: slot-leader status (dependent books only) */}
      {leaders && leaders.length > 0 && (
        <div style={{ marginTop: 4 }}><SlotLeaders leaders={leaders} compact /></div>
      )}
    </div>
  );
}
